import {
    type ShotgunSlotOrder,
    matchesShotgunOrder,
    normalizeShotgunOrder,
} from '@moment-golf/shared';

export type { ShotgunSlotOrder };
export { matchesShotgunOrder, normalizeShotgunOrder };

export type ShotgunHoleSlot = {
    holeNumber: number;
    shotgunOrder: ShotgunSlotOrder;
    label: string;
};

export function formatHoleSlotLabel(holeNumber: number, slotOrder: ShotgunSlotOrder): string {
    if (slotOrder === 'A') return `${holeNumber}A`;
    if (slotOrder === 'B') return `${holeNumber}B`;
    return String(holeNumber);
}

export function resolveRoundHoles(
    viewEvent: any,
    viewEventData: any,
    roundValue: number,
): any[] {
    if (!viewEvent) return [];
    const currentRound = viewEvent.rounds?.[roundValue - 1];
    if (!currentRound) return [];

    const courseId = currentRound.courseID ?? viewEvent.courseID;
    const course = viewEventData?.courseData?.find(
        (c: any) => String(c._id) === String(courseId),
    );
    const roundTeeSetName = currentRound.teeSetNames;
    const selectedTeeSet = roundTeeSetName
        ? course?.teeSets?.find((ts: any) => ts.teeName === roundTeeSetName) ?? course?.teeSets?.[0]
        : course?.teeSets?.[0];
    return selectedTeeSet?.holes ?? [];
}

export function computeShotgunTeeTimeCount(viewEvent: any, roundValue: number): number {
    const currentRound = viewEvent?.rounds?.[roundValue - 1];
    const regPerTee = currentRound?.regPerTeeTime ?? viewEvent?.regPerTeeTime ?? 4;
    const playerCap = viewEvent?.playerCap ?? 0;
    return Math.ceil(playerCap / regPerTee);
}

export function buildShotgunHoleSlots(holes: any[], teeTimeCount: number): ShotgunHoleSlot[] {
    const slots: ShotgunHoleSlot[] = [];
    let rowCount = 0;

    for (let holeIndex = 0; holeIndex < holes.length && rowCount < teeTimeCount; holeIndex++) {
        const hole = holes[holeIndex];
        if (hole.par === 5) {
            if (rowCount < teeTimeCount) {
                slots.push({
                    holeNumber: hole.number,
                    shotgunOrder: 'A',
                    label: formatHoleSlotLabel(hole.number, 'A'),
                });
                rowCount++;
            }
            if (rowCount < teeTimeCount) {
                slots.push({
                    holeNumber: hole.number,
                    shotgunOrder: 'B',
                    label: formatHoleSlotLabel(hole.number, 'B'),
                });
                rowCount++;
            }
        } else {
            slots.push({
                holeNumber: hole.number,
                shotgunOrder: null,
                label: formatHoleSlotLabel(hole.number, null),
            });
            rowCount++;
        }
    }

    return slots;
}

export function buildDynamicShotgunHoleSlots(
    holes: any[],
    registrations: any[],
    roundValue: number,
): ShotgunHoleSlot[] {
    const slots: ShotgunHoleSlot[] = [];

    for (const hole of holes) {
        const hasBPlayers = filterRegsForShotgunSlot(registrations, roundValue, hole.number, 'B').length > 0;
        if (hasBPlayers) {
            slots.push({
                holeNumber: hole.number,
                shotgunOrder: 'B',
                label: formatHoleSlotLabel(hole.number, 'B'),
            });
        }

        slots.push({
            holeNumber: hole.number,
            shotgunOrder: 'A',
            label: formatHoleSlotLabel(hole.number, 'A'),
        });
    }

    return slots;
}

export function getShotgunSlotSortIndex(
    viewEvent: any,
    viewEventData: any,
    roundValue: number,
    holeNumber: number | null | undefined,
    shotgunOrder: string | null | undefined,
): number {
    if (holeNumber == null || !Number.isFinite(Number(holeNumber))) {
        return 9999;
    }

    const holes = resolveRoundHoles(viewEvent, viewEventData, roundValue);
    const teeTimeCount = computeShotgunTeeTimeCount(viewEvent, roundValue);
    const slots = buildShotgunHoleSlots(holes, teeTimeCount);
    const normalizedOrder = normalizeShotgunOrder(shotgunOrder);

    const idx = slots.findIndex(
        (slot) =>
            slot.holeNumber === holeNumber &&
            slot.shotgunOrder === normalizedOrder,
    );
    if (idx >= 0) return idx;

    const orderSuffix = normalizedOrder === 'A' ? 0 : normalizedOrder === 'B' ? 1 : 2;
    return Number(holeNumber) * 10 + orderSuffix;
}

export function filterRegsForShotgunSlot(
    registrations: any[],
    roundValue: number,
    holeNumber: number,
    slotOrder: ShotgunSlotOrder,
): any[] {
    return registrations.filter((registration: any) => {
        const hasTeeTimes = registration.teeTimes && Array.isArray(registration.teeTimes);
        if (!hasTeeTimes) return false;
        return registration.teeTimes.some(
            (teeTime: any) =>
                teeTime.round === roundValue &&
                teeTime.teeTime &&
                teeTime.startingHole === holeNumber &&
                matchesShotgunOrder(teeTime.shotgunOrder, slotOrder),
        );
    });
}

export function filterScoresForShotgunSlot(
    scores: any[],
    registrations: any[],
    roundValue: number,
    holeNumber: number,
    slotOrder: ShotgunSlotOrder,
): any[] {
    const roundIdx = roundValue - 1;
    return scores.filter((score: any) => {
        const rs = score.roundScores?.[roundIdx];
        if (!rs?.teeTime || rs.startingHole == null) return false;
        return (
            rs.startingHole === holeNumber &&
            matchesShotgunOrder(rs.shotgunOrder, slotOrder)
        );
    }).map((score: any) => {
        const matchingReg = registrations.filter(
            (reg: any) => reg.eventScoreID === score._id.toString(),
        );
        return {
            ...score,
            playerName: matchingReg.map((reg: any) => reg.playerName).join('/') || '',
            teamName: matchingReg[0]?.teamName || '',
        };
    });
}
